export type ApiUser = { id: string; displayName: string | null; email: string | null };
export type ApiOrg = { id: string; name: string; domainPrefix: string | null };

export type Column = {
  id: string;
  label: string;
  type: string;
  group: string;
  default: boolean;
  sortable?: boolean;
  choices?: string[];
};

export type Registrant = {
  id: string;
  // The attendee's own contact id, or null when nothing told us one.
  // Never the buyer's — that stays in purchaserContactId. See
  // src/core/attendee-overrides.ts.
  contactId: string | null;
  eventId: string;
  eventName: string;
  ticketCode: string | null;
  ticketType: string | null;
  registeredAt: string;
  fullName: string;
  firstName: string | null;
  lastName: string | null;
  purchaserName: string | null;
  purchaserContactId: string | null;
  purchaserEmail: string | null;
  purchaserPhone: string | null;
  email: string | null;
  phone: string | null;
  gender: string | null;
  dateOfBirth: string | null;
  paid: boolean;
  amountPaid: number;
  amountOwing: number;
  membershipActive: boolean;
  membershipLevel: string | null;
  custom: Record<string, unknown>;
  // false when this row's attendee is someone other than the buyer and
  // we have no contact record for them: the blank fields above are
  // unknowns, not empties. See RegistrantDrawer.
  attendeeContactKnown: boolean;
};

export type SeriesPoint = { label: string; value: number };

export type RegistrantsResponse = {
  events: { id: string; name: string; startsAt: string | null; endsAt: string | null }[];
  registrants: Registrant[];
  columns: Column[];
  defaultVisibleColumnIds: string[];
  summary: {
    totalRegistrants: number;
    paidCount: number;
    paidPercent: number;
    totalPaid: number;
    totalOwing: number;
    membersCount: number;
    membersPercent: number;
    genderSplit: Record<string, number>;
  };
  charts: {
    signupsByWeek: SeriesPoint[];
    signupsCumulative: SeriesPoint[];
    ageHistogram: SeriesPoint[];
    genderDonut: SeriesPoint[];
  };
};

export type AttendeeUpload = {
  eventId: string;
  rowCount: number;
  // Split by where each row came from. A webhook row carries the
  // attendee's TidyHQ contact id, so it resolves their real record; a
  // CSV row can only ever carry what the export printed.
  csvRowCount: number;
  webhookRowCount: number;
  // CSV rows whose attendee we since matched to a TidyHQ contact by
  // email and name, so they resolve a real record too.
  resolvedRowCount: number;
  uploadedAt: number;
};

export type ReplaySummary = {
  scanned: number;
  packActivations: number;
  applied: number;
  skippedAlreadyKnown: number;
  ticketsUpserted: number;
  byEvent: Array<{ eventId: string; tickets: number }>;
  usedUnfilteredFallback: boolean;
  hitLimit: boolean;
};

export type WebhookRegistration = {
  id: string;
  orgId: string;
  tidyhqWebhookId: string | null;
  matchingKind: string;
  url: string;
  description: string | null;
  hasSigningKey: boolean;
  allowStateChanges: boolean;
  status: string | null;
  createdAt: number;
};

export type WebhookEventSummary = {
  id: string;
  registrationId: string;
  tidyhqEventId: string | null;
  kind: string;
  object: string | null;
  createdTs: number | null;
  receivedAt: number;
  signatureValid: boolean;
  signatureMatchedHeader: string | null;
  payload: unknown;
};

export type SavedView = {
  id: string;
  orgId: string;
  name: string;
  filters: Record<string, unknown>;
  visibleColumnIds: string[];
  columnOrder: string[] | null;
  sort: { columnId: string; dir: "asc" | "desc" } | null;
  createdBy: string;
  createdAt: number;
};

async function get<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "same-origin" });
  if (r.status === 401) throw new Error("unauthenticated");
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return r.json() as Promise<T>;
}

export const api = {
  me: () => get<{ user: ApiUser; instance: string }>("/api/me"),
  org: () => get<ApiOrg>("/api/org"),
  registrants: () => get<RegistrantsResponse>("/api/registrants"),
  views: () => get<SavedView[]>("/api/views"),
  saveView: async (input: { name: string; filters: Record<string, unknown>; visibleColumnIds: string[]; columnOrder: string[] | null; sort: { columnId: string; dir: "asc" | "desc" } | null }): Promise<SavedView> => {
    const r = await fetch("/api/views", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
      credentials: "same-origin",
    });
    if (!r.ok) throw new Error(`save view failed: ${r.status}`);
    return r.json() as Promise<SavedView>;
  },
  updateView: async (id: string, input: { name: string; filters: Record<string, unknown>; visibleColumnIds: string[]; columnOrder: string[] | null; sort: { columnId: string; dir: "asc" | "desc" } | null }): Promise<SavedView> => {
    const r = await fetch(`/api/views/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
      credentials: "same-origin",
    });
    if (!r.ok) throw new Error(`update view failed: ${r.status}`);
    return r.json() as Promise<SavedView>;
  },
  deleteView: async (id: string) => {
    const r = await fetch(`/api/views/${encodeURIComponent(id)}`, { method: "DELETE", credentials: "same-origin" });
    if (!r.ok && r.status !== 204) throw new Error(`delete view failed: ${r.status}`);
  },
  logout: async () => {
    await fetch("/auth/logout", { method: "POST", credentials: "same-origin" });
  },
  attendees: {
    list: () => get<{ uploads: AttendeeUpload[] }>("/api/attendees"),
    upload: async (eventId: string, file: File | Blob): Promise<{ upload: AttendeeUpload; errors: string[] }> => {
      const r = await fetch(`/api/attendees/uploads?event_id=${encodeURIComponent(eventId)}`, {
        method: "POST",
        headers: { "Content-Type": "text/csv" },
        body: file,
        credentials: "same-origin",
      });
      const body = (await r.json().catch(() => null)) as
        | { upload: AttendeeUpload; errors: string[] }
        | { error: string; details?: string[] }
        | null;
      if (!r.ok || !body || !("upload" in body)) {
        const errBody = body as { error?: string; details?: string[] } | null;
        const detail = errBody?.details?.length ? errBody.details.join("; ") : (errBody?.error ?? `HTTP ${r.status}`);
        throw new Error(detail);
      }
      return body;
    },
    remove: async (eventId: string) => {
      const r = await fetch(`/api/attendees/${encodeURIComponent(eventId)}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!r.ok && r.status !== 204) throw new Error(`delete failed: ${r.status}`);
    },
    resync: async (eventId?: string): Promise<{ replay: ReplaySummary; uploads: AttendeeUpload[] }> => {
      const qs = eventId ? `?event_id=${encodeURIComponent(eventId)}` : "";
      const r = await fetch(`/api/attendees/resync${qs}`, { method: "POST", credentials: "same-origin" });
      const body = (await r.json().catch(() => null)) as
        | { replay: ReplaySummary; uploads: AttendeeUpload[] }
        | { error: string }
        | null;
      if (!r.ok || !body || !("replay" in body)) {
        throw new Error((body as { error?: string } | null)?.error ?? `HTTP ${r.status}`);
      }
      return body;
    },
  },
  webhooks: {
    list: () => get<{ registrations: WebhookRegistration[] }>("/api/webhooks"),
    create: async (input: { matchingKind: string; description?: string }): Promise<WebhookRegistration> => {
      const r = await fetch("/api/webhooks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
        credentials: "same-origin",
      });
      const body = (await r.json().catch(() => null)) as { registration: WebhookRegistration } | { error: string } | null;
      if (!r.ok || !body || !("registration" in body)) {
        throw new Error(((body as { error?: string })?.error) ?? `create failed: ${r.status}`);
      }
      return body.registration;
    },
    remove: async (id: string) => {
      const r = await fetch(`/api/webhooks/${encodeURIComponent(id)}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!r.ok && r.status !== 204) throw new Error(`delete failed: ${r.status}`);
    },
    setActive: async (id: string, active: boolean): Promise<WebhookRegistration> => {
      const path = active ? "activate" : "deactivate";
      const r = await fetch(`/api/webhooks/${encodeURIComponent(id)}/${path}`, {
        method: "POST",
        credentials: "same-origin",
      });
      if (!r.ok) throw new Error(`state change failed: ${r.status}`);
      return (await r.json() as { registration: WebhookRegistration }).registration;
    },
    events: (limit = 100) => get<{ events: WebhookEventSummary[] }>(`/api/webhooks/events?limit=${limit}`),
    triggered: (opts: { kinds?: string[]; limit?: number } = {}) => {
      const qs = new URLSearchParams();
      for (const k of opts.kinds ?? []) qs.append("kind", k);
      if (opts.limit) qs.set("limit", String(opts.limit));
      const suffix = qs.toString();
      return get<{ events: Array<{ id?: string; kind: string; object?: string; created?: number; data?: unknown }> }>(
        `/api/webhooks/triggered${suffix ? `?${suffix}` : ""}`,
      );
    },
  },
};
